import { createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import { Map } from 'eemovel-map-library';
import { IPagination } from '../../../../component';
import { RootState } from '../../../../reducer';
import { GUIDE_STEPS } from './studies.constants';
import {
  IsDropDownOpenMapper,
  LayoutValuationResponse,
  LayoutValuationPayload,
  PosObj,
  Study,
  GuideStepsKeys,
  VGV,
  PolygonType,
  StudiesState,
  QualifiedAudience,
  VGVResponse,
  InstallmentsSimulation,
  AbsorptionAndLiquidityData,
} from './studies.model';
import { studiesInitialState } from './studies.state';

const studiesSlice = createSlice({
  name: 'studiesState',
  initialState: studiesInitialState,
  reducers: {
    setStudiesList(state, { payload }: PayloadAction<Study[] | null>) {
      state.studiesList = payload;
    },
    setSelectedStudy(state, { payload }: PayloadAction<Study | null>) {
      state.selectedStudy = payload;
    },
    setIsDropdownOpen(state, { payload }: PayloadAction<IsDropDownOpenMapper>) {
      state.isDropdownOpen = { ...state.isDropdownOpen, ...payload };
    },
    setPagination(state, { payload }: PayloadAction<IPagination>) {
      state.pagination = payload;
    },
    advanceToStep(state, { payload }: PayloadAction<GuideStepsKeys>) {
      state.currentStep = GUIDE_STEPS[payload];
    },
    setCanAdvance(state, { payload }: PayloadAction<boolean>) {
      state.currentStep = { ...state.currentStep, canAdvance: payload };
    },
    setIsDragging(state, { payload }: PayloadAction<boolean>) {
      state.isDragging = payload;
    },
    changePositionTo(state, { payload }: PayloadAction<PosObj>) {
      state.translatePos = payload;
    },
    setCurrentProgressTo(state, { payload }: PayloadAction<number>) {
      state.currentProgress = payload;
    },
    setLayoutsValuationPayload(
      state,
      { payload }: PayloadAction<LayoutValuationPayload>
    ) {
      state.layoutsValuationPayload = payload;
    },
    setLayoutsValuationPrimaryResponse(
      state,
      { payload }: PayloadAction<LayoutValuationResponse>
    ) {
      state.layoutsValuationPrimaryResponse = payload;
    },
    setLayoutsValuationSecondaryResponse(
      state,
      { payload }: PayloadAction<LayoutValuationResponse | null>
    ) {
      state.layoutsValuationSecondaryResponse = payload;
    },
    setLayoutsVGVs(state, { payload }: PayloadAction<VGVResponse[]>) {
      formattedVGVs(payload, state);
    },
    setPolygon<K extends PolygonType>(
      state: StudiesState,
      { payload }: PayloadAction<K>
    ) {
      state.polygon = payload;
    },
    setInstallmentsSimulationPrimary(
      state,
      { payload }: PayloadAction<InstallmentsSimulation[]>
    ) {
      state.installmentsSimulationPrimary = payload;
    },
    setInstallmentsSimulationSecondary(
      state,
      { payload }: PayloadAction<InstallmentsSimulation[]>
    ) {
      state.installmentsSimulationSecondary = payload;
    },
    setStudyName(state, { payload }: PayloadAction<string>) {
      state.studyName = payload;
    },
    setCreationDate(state, { payload }: PayloadAction<string>) {
      state.creationDate = payload;
    },
    setId(state, { payload }: PayloadAction<string>) {
      state.id = payload;
    },
    setExpandedAreaPolygon(
      state,
      { payload }: PayloadAction<google.maps.Circle | null>
    ) {
      state.expandedArea = payload;
    },
    setStudy(state, { payload }: PayloadAction<Study>) {
      state.absorptionAndLiquidityResponsePrimary =
        payload.absorption_liquidity?.primary_market ?? null;
      state.absorptionAndLiquidityResponseSecondary =
        payload.absorption_liquidity?.secondary_market &&
        payload.absorption_liquidity?.secondary_market.length > 0
          ? payload.absorption_liquidity?.secondary_market
          : null;
      state.polygon = payload.coordinates
        ? buildPolygon(payload.coordinates.location)
        : null;
      state.layoutsValuationPrimaryResponse = payload.primary_market ?? null;
      state.layoutsValuationSecondaryResponse =
        payload?.secondary_market && payload?.secondary_market?.length > 0
          ? payload.secondary_market
          : null;
      if (payload.vgv_primary_secondary) {
        formattedVGVs(payload.vgv_primary_secondary, state);
      }
      state.qualifiedAudience = payload.perfil_socio;
      state.transactionedMarketCycle = payload.transactioned_market_cycle;
      state.id = payload.id;
      state.layoutsValuationPayload = payload.layoutCharacteristics;
      state.studyName = payload.name;
      state.installmentsSimulationPrimary =
        payload.installmentsSimulationPrimary;
      state.installmentsSimulationSecondary =
        payload.installmentsSimulationSecondary ?? null;
    },
    setQualifiedAudience(state, { payload }: PayloadAction<QualifiedAudience>) {
      state.qualifiedAudience = payload;
    },
    setAbsorptionLiquidityResponsePrimary(
      state,
      { payload }: PayloadAction<AbsorptionAndLiquidityData[]>
    ) {
      state.absorptionAndLiquidityResponsePrimary = payload;
    },
    setAbsorptionLiquidityResponseSecondary(
      state,
      { payload }: PayloadAction<AbsorptionAndLiquidityData[]>
    ) {
      state.absorptionAndLiquidityResponseSecondary = payload;
    },
    setInitialStep(state, { payload }: PayloadAction<GuideStepsKeys | null>) {
      state.initialStep = payload;
    },
    closeAllDropdowns(state, { payload }: PayloadAction<void>) {
      let closedDDs = {};
      state.studiesList?.forEach(
        (study, index) => (closedDDs = { ...closedDDs, [index]: false })
      );
      state.isDropdownOpen = closedDDs;
    },
    setAuthor(state, { payload }: PayloadAction<string>) {
      state.author = payload;
    },
    setTitleSearchInput(state, { payload }: PayloadAction<string>) {
      state.titleSearchInput = payload;
    },
  },
});

export const {
  setStudiesList,
  setSelectedStudy,
  setIsDropdownOpen,
  setPagination,
  advanceToStep,
  setCanAdvance,
  setIsDragging,
  changePositionTo,
  setCurrentProgressTo,
  setLayoutsValuationPayload,
  setLayoutsValuationPrimaryResponse,
  setLayoutsValuationSecondaryResponse,
  setLayoutsVGVs,
  setPolygon,
  setInstallmentsSimulationPrimary,
  setInstallmentsSimulationSecondary,
  setStudyName,
  setCreationDate,
  setId,
  setExpandedAreaPolygon,
  setStudy,
  setQualifiedAudience,
  setAbsorptionLiquidityResponsePrimary,
  setAbsorptionLiquidityResponseSecondary,
  setInitialStep,
  closeAllDropdowns,
  setAuthor,
  setTitleSearchInput,
} = studiesSlice.actions;

export const studiesSelector = (state: RootState) => state.studies;

export default studiesSlice.reducer;

const buildPolygon = ({
  coordinates,
  radius,
}: {
  coordinates: number[][];
  radius?: number;
}) => {
  const LatLng = coordinates.map((el) => new google.maps.LatLng(el[0], el[1]));
  if (radius) {
    const circle = new google.maps.Circle({
      center: LatLng[0],
      radius,
      map: Map.map,
    });
    Map.markerCircle = circle;
    return circle;
  }
  const polygon = new google.maps.Polygon({
    paths: LatLng,
    fillColor: '#000',
    map: Map.map,
  });
  Map.PoligonOnMap = polygon;
  return polygon;
};

const formattedVGVs = (payload: any, state: any) => {
  payload.forEach((vgv: any, index: number) => {
    const formattedVGV: VGV = {
      numberOfUnits: vgv.quantity,
      primaryMarketVGV: vgv.vgv_primary_market,
      secondaryMarketVGV:
        vgv.vgv_secondary_market > 0 ? vgv.vgv_secondary_market : undefined,
      id: vgv.id,
    };
    if (state.layoutsValuationPrimaryResponse) {
      const currentLayout = state.layoutsValuationPrimaryResponse.findIndex(
        (el: any) => el.id === vgv.id
      );
      if (currentLayout !== -1) {
        state.layoutsValuationPrimaryResponse[currentLayout] = {
          ...state.layoutsValuationPrimaryResponse[currentLayout],
          vgv: formattedVGV,
        };
      }
    }
    if (state.layoutsValuationSecondaryResponse) {
      const currentLayout = state.layoutsValuationSecondaryResponse.findIndex(
        (el: any) => el.id === vgv.id
      );
      if (currentLayout !== -1) {
        state.layoutsValuationSecondaryResponse[currentLayout] = {
          ...state.layoutsValuationSecondaryResponse[currentLayout],
          vgv: formattedVGV,
        };
      }
    }
  });
};
